import React, { useCallback, useEffect, useRef, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

import classNames from 'classnames';
import {matchPath, Route, useLocation, useHistory} from 'react-router-dom';
import { ReactComponent as ArrowBackSvg } from '../../assets/svg/arrow_back.svg';
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { isMobile } from '../../shared/helpers';
import { iFullStoreState } from '../../shared/interfaces';
import { getRecentSearches, addRecentSearch } from '../../shared/recentSearches';

// Redux Imports
import { useRedux } from "../../states/redux-state";
import { DashboardSize } from '../../stores/reducers/dashboardInfo';
import { closeDashboard, openDashboard, openSmallDashboard } from '../../stores/reducers/dashboardInfo/AC';
import { Actions as GeneralActions } from '../../stores/reducers/general-reducers';

// UI Components
import BookmarksBar from "../BookmarksBar";
import SearchModal from '../menus/search';
import { AdvancedSearchModal, AdvancedSearchCondition, ADVANCED_SEARCH_PREFIX } from '../AdvancedSearchModal';
import ChevronRightIcon from '../SVG-components/chevron_right';
import SearchIcon from '../SVG-components/search';

// Styles
import './styles.scss';
import LegendButton from "../LegendButton";

export const SearchInput = ({ visibleInput, extraActions = null }: { visibleInput: boolean, extraActions?: JSX.Element }) => {
  const dispatch = useDispatch();
  const searchTerm = useSelector<iFullStoreState, string>(state => state.general.searchTerm);
  const isSearchVis = useSelector<iFullStoreState, boolean>(s => s.general.showSearch);
  const [localValue, setLocalValue] = useState(searchTerm);

  // Update local state when Redux state changes (e.g., from clear/reset)
  useEffect(() => {
    setLocalValue(searchTerm);
  }, [searchTerm]);

  // Debounced Redux dispatch to trigger search
  const debouncedDispatch = useCallback(
    debounce((value: string) => {
      dispatch(GeneralActions.CHANGE_TERM(value));
    }, 300),
    [dispatch]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedDispatch.cancel();
    };
  }, [debouncedDispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Update immediately for user feedback
    debouncedDispatch(newValue); // Trigger search after delay
  }

  return (
    <div className="search">
      <div className={`search-input ${visibleInput ? 'open' : ''}`}>
        {!visibleInput && extraActions}

        <input
          type="text"
          id="search-input"
          value={localValue}
          onChange={handleChange}
        />

        <label htmlFor="search-input" onClick={() => dispatch(GeneralActions.SHOW_SEARCH)}>
          <SearchIcon />
        </label>

        {isSearchVis && (
          <label
            className="close-icon"
            onClick={() => {
              dispatch(GeneralActions.HIDE_SEARCH);
              dispatch(GeneralActions.CHANGE_TERM(''));
              dispatch(GeneralActions.CHANGE_SEARCH_BY(''));
            }}
          >
            <PlusIcon />
          </label>
        )}
      </div>
    </div>
  );
};

export const SearchInputDesktop = ({ style = {}, className = '' }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const searchTerm = useSelector<iFullStoreState, string>(state => state.general.searchTerm);
  const isSearchVis = useSelector<iFullStoreState, boolean>(s => s.general.showSearch);
  const [localValue, setLocalValue] = useState(searchTerm);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [recentSearches, setRecentSearches] = useState(getRecentSearches());
  const inputRef = useRef<HTMLInputElement>(null);
  const previousIsSearchVis = useRef(isSearchVis);
  const lastNonEmptyTermRef = useRef('');
  const isAdvancedSearchActive = (searchTerm || '').startsWith(ADVANCED_SEARCH_PREFIX);

  const isExtraInfoPage = location.pathname === '/extra-info';
  const isReportsPage = location.pathname.startsWith('/reports');
  const isTagsPage = location.pathname === '/tags' || location.pathname.startsWith('/tag/');
  const isPersonDetailPage = location.pathname.startsWith('/person/');

  const shouldDisableSuggestions =
    location.pathname === '/person' ||
    location.pathname === '/tags' ||
    location.pathname.includes('/reports/');

  // Update local state when Redux state changes (e.g., from clear/reset)
  useEffect(() => {
    setLocalValue(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    const trimmed = (searchTerm || '').trim();
    if (trimmed.length > 0) {
      lastNonEmptyTermRef.current = trimmed;
    }
  }, [searchTerm]);

  // Debounced Redux dispatch to trigger search
  const debouncedDispatch = useCallback(
    debounce((value: string) => {
      dispatch(GeneralActions.CHANGE_TERM(value));
    }, 300),
    [dispatch]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedDispatch.cancel();
    };
  }, [debouncedDispatch]);

  useEffect(() => {
    if (previousIsSearchVis.current && !isSearchVis && lastNonEmptyTermRef.current) {
      addRecentSearch(lastNonEmptyTermRef.current);
      setRecentSearches(getRecentSearches());
    }
    previousIsSearchVis.current = isSearchVis;
  }, [isSearchVis]);

  // Close suggestions when search results appear
  useEffect(() => {
    if (isSearchVis) {
      setShowSuggestions(false);
    }
  }, [isSearchVis]);

  const handleInput = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedDispatch(newValue);
    if (!isSearchVis && newValue.length > 0) {
      dispatch(GeneralActions.SHOW_SEARCH);
    }
    setShowSuggestions(false);
  }

  const handleFocus = () => {
    const recent = getRecentSearches();
    setShowSuggestions(true);
    setRecentSearches(shouldDisableSuggestions ? [] : recent);
  }

  const handleBlur = () => {
    // Save search if not empty
    if (localValue && localValue.trim().length > 0) {
      addRecentSearch(localValue);
      setRecentSearches(getRecentSearches());
    }
    // Small delay to allow clicking on suggestion
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && localValue && localValue.trim().length > 0) {
      addRecentSearch(localValue);
      setRecentSearches(getRecentSearches());
      setShowSuggestions(false);
    }
  }

  const handleSuggestionClick = (term: string) => {
    setLocalValue(formatRecentSearchDisplay(term));
    setShowSuggestions(false);
    if (!isSearchVis) {
      dispatch(GeneralActions.SHOW_SEARCH);
    }
    dispatch(GeneralActions.CHANGE_TERM(term));
    addRecentSearch(term);
    setRecentSearches(getRecentSearches());
  }

  const formatRecentSearchDisplay = (term: string) => {
    if (!term?.startsWith(ADVANCED_SEARCH_PREFIX)) {
      return term;
    }

    try {
      const parsed = JSON.parse(term.slice(ADVANCED_SEARCH_PREFIX.length));
      if (!Array.isArray(parsed) || parsed.length === 0) return 'Advanced Search';

      const operatorMap = {
        startsWith: 'starts with',
        contains: 'contains',
        equals: 'equals',
        greaterThan: '>',
        lessThan: '<',
      };

      const summary = parsed
        .map((c: any) => `${c.fieldLabel || c.fieldId} ${operatorMap[c.operator] || c.operator} ${c.value}`)
        .join(' • ');

      return summary;
    } catch {
      return 'Search';
    }
  }

  const handleAdvancedSearch = (conditions: AdvancedSearchCondition[]) => {
    if (!conditions?.length) return;

    const encodedSearch = `${ADVANCED_SEARCH_PREFIX}${JSON.stringify(conditions)}`;
    setLocalValue(formatRecentSearchDisplay(encodedSearch));
    setShowAdvancedSearch(false);
    setShowSuggestions(false);

    if (!isSearchVis) {
      dispatch(GeneralActions.SHOW_SEARCH);
    }

    dispatch(GeneralActions.CHANGE_TERM(encodedSearch));
  }

  const hideSearch = () => {
    if (isSearchVis) {
      debouncedDispatch.cancel();
      setLocalValue('');
      dispatch(GeneralActions.HIDE_SEARCH)
      dispatch(GeneralActions.CHANGE_TERM(''));
      dispatch(GeneralActions.CHANGE_SEARCH_BY(''))
    }
  }

  const handleGoBack = () => {
    history.goBack();
  }

  return (
    <div
      className={classNames(
        className,
        'searchBlock',
        { 'isOpen': isSearchVis },
        { 'hasBackArrow': isExtraInfoPage && !isSearchVis },
        { 'isReportsPage': isReportsPage },
        { 'isTagsPage': isTagsPage || isPersonDetailPage }
      )}
      style={style}
    >
      {isSearchVis && (
        <button
          className="backIcon"
          onClick={hideSearch}
        >
          <ArrowBackSvg style={{ position: 'absolute', top: 0, left: 0 }} />
        </button>
      )}

      {isExtraInfoPage && !isSearchVis && (
        <button
          className="backIconExtraInfo"
          onClick={handleGoBack}
          title="Go back to Tags"
        >
          <ArrowBackSvg style={{ color: '#FF7C02' }} />
        </button>
      )}

      <div className="searchContainer">
        <SearchIcon className='searchIcon' />

        <input
          ref={inputRef}
          className={`searchInput ${isSearchVis ? "isOpen" : ""}`}
          type="text"
          id="search-input"
          value={localValue}
          onChange={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder='Search'
          autoFocus={isSearchVis}
        />

        {showSuggestions && (
          <div className="recentSearchesDropdown">
            <div className="recentSearchesHeader">Recent Searches</div>
            {recentSearches.map((search) => (
              <div
                key={search.term}
                className="recentSearchItem"
                onClick={() => handleSuggestionClick(search.term)}
              >
                <span>{formatRecentSearchDisplay(search.term)}</span>
              </div>
            ))}
            <button
              type="button"
              className="advancedSearchLink"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setShowAdvancedSearch(true);
                setShowSuggestions(false);
              }}
            >
              Advanced Search
            </button>
          </div>
        )}

        <AdvancedSearchModal
          open={showAdvancedSearch}
          onClose={() => setShowAdvancedSearch(false)}
          onSearch={handleAdvancedSearch}
        />
      </div>
    </div>
  );
};

type IProps = {
  overlay?: JSX.Element;
  title?: string;
  searchExtra?: JSX.Element;
  className?: string;
  style?: React.CSSProperties;
  parentStyle?: React.CSSProperties;
  header?: React.ReactNode;
}

export let scrollContainerEl: HTMLElement | null = null;

export const setScrollContainerEl = (el: HTMLElement | null) => {
  scrollContainerEl = el;
};

const DashboardBlock: React.FunctionComponent<IProps> = ({ children, overlay, title = '', searchExtra = null, className, style, parentStyle = undefined, header }) => {
  const user = useRedux((s) => s.auth.user);

  const size = useSelector<iFullStoreState, DashboardSize>((s) => s.dashboardInfo.size);
  const isSearchVis = useSelector<iFullStoreState, boolean>((s) => s.general.showSearch);
  const isBookmarksSearchVis = useSelector<iFullStoreState, boolean>((s) => s.general.showBookmarksSearch);

  let open = size !== DashboardSize.CLOSED ? ' open' : '';

  const params = useLocation();

  if (size === DashboardSize.OPEN_FULL) {
    open += ' full-size';
  }

  if (size === DashboardSize.OPEN_SMALL) {
    open += ' small';
  }

  const [showScroll, setShowScroll] = useState(false);

  const route = ["/billing/units", "/billing/invoices", "/billing/orders"];
  const billingRoute = route.some((item) => item === params.pathname)
  const IS_PUBLIC_VIEW = window.location.href.includes('public-view');

  const dispatch = useDispatch();

  useEffect(() => {
    if (!IS_PUBLIC_VIEW) {
      scrollContainerEl?.scrollTo?.({ top: 0 });
      setShowScroll(false);
    }
  }, []);

  useEffect(() => {
    const allowedPathsForSmall = ['/device', '/person'];
    const shouldOpenSmall = allowedPathsForSmall.includes(params.pathname);

    if (shouldOpenSmall) {
      dispatch(openSmallDashboard());
    } else {
      if (size !== DashboardSize.OPEN) {
        dispatch(openDashboard());
      }
    }
  }, [params.pathname]);

  type BookmarksSearchValues = {
    devicesSearchValue: string;
    peopleSearchValue: string;
    mapSearchValue: string;
  };

  const bookmarksSearchDefaulValues: BookmarksSearchValues = {
    devicesSearchValue: '',
    peopleSearchValue: '',
    mapSearchValue: ''
  };

  const [bookmarksSearchValue, setBookmarksSearchValue] = useState<BookmarksSearchValues>(bookmarksSearchDefaulValues);

  useEffect(() => {
    if (!isBookmarksSearchVis) {
      setBookmarksSearchValue(bookmarksSearchDefaulValues);
    }
  }, [isBookmarksSearchVis]);

  const hideBookmarksSearch = () => {
    if (isBookmarksSearchVis) {
      dispatch(GeneralActions.HIDE_BOOKMARKS_SEARCH)
      dispatch(GeneralActions.HIDE_SEARCH);
      dispatch(GeneralActions.CHANGE_TERM(''))
      dispatch(GeneralActions.CHANGE_SEARCH_BY(''))
      setBookmarksSearchValue(bookmarksSearchDefaulValues);
    }
  }


  const isDevicePage = !!matchPath(params.pathname, {
    path: ['/device/:id', '/device/:id/edit'],
    exact: true,
  });
  const isDevicesOrPeoplePage = params.pathname.includes('/device') || params.pathname === '/person';
  const shouldShowOutsideDevicesAndPeoplePagesOrWhenSearchVisible = !isDevicesOrPeoplePage || isSearchVis || isDevicePage;
  const isCordova = isMobile || window['cordova'];

  // !window['cordova']
  return !IS_PUBLIC_VIEW && (
    <div
      className={
        classNames(
          `dashboard-block`,
          open,
          { isDevicePage: !!isDevicePage && !isSearchVis },
          { isDevicesOrPeople: isDevicesOrPeoplePage },
        )
      }
      style={parentStyle}
      onClick={() => setShowScroll(true)}
    >
      {(isMobile || window['cordova']) && title !== '' && (
        <h1>{title}</h1>
      )}

      {
        (!billingRoute && !isBookmarksSearchVis && !isCordova)
        && shouldShowOutsideDevicesAndPeoplePagesOrWhenSearchVisible
        && !(isDevicePage && !!header)
        && <SearchInputDesktop style={style} className={className} />
      }

      {isBookmarksSearchVis && (
        <button
          className='hideBookmarksSearchButton'
          onClick={hideBookmarksSearch}
        >
          <ArrowBackSvg style={{ position: 'absolute', top: 0, left: 0, color: '#FF7C02' }} />
        </button>
      )}

      {!isBookmarksSearchVis && header}

      <PerfectScrollbar
        className={classNames(
          'scrollable',
          {
            'scrollable_aboveAll': !billingRoute && isBookmarksSearchVis,
            'scrollable_fullHeight': isDevicesOrPeoplePage,
          },
          className,
        )}
        options={{ suppressScrollX: true }}
        containerRef={setScrollContainerEl}
        style={style}
      >
        {!billingRoute && (isSearchVis || isBookmarksSearchVis) ? (
          <SearchModal />
        ) : (
          children
        )}
      </PerfectScrollbar>

      {overlay}

      {!(isMobile) && <DashboardExpanderButtons />}

      <div className='bookmarks-bar-container'>
        <BookmarksBar
          bookmarksSearchValue={bookmarksSearchValue}
          setBookmarksSearchValue={setBookmarksSearchValue}
        />
      </div>
    </div>
  );
};

export default DashboardBlock;

const DashboardExpanderButtons = () => {
  const dispatch = useDispatch();

  return (
    <div>
        <div className="expander-buttons">
            <div className="increase-button" onClick={() => dispatch(openDashboard())}>
                <ChevronRightIcon />
            </div>
            <span />
            <div className="decrease-button" onClick={() => { dispatch(closeDashboard()) }}>
                <ChevronRightIcon />
            </div>
        </div>

        {/* Commented this section due to MAS-1630 */}
        {/* <Route exact path="/device/">
            <div className="legend-button">
                <LegendButton/>
            </div>
        </Route> */}
    </div>
  );
};
